import moment from 'moment'
import { normalizeDateFormat } from 'common/helpers'
import { isInteger } from 'common/helpers/primitives/isType'
import resolveEndpoint from 'sb-request/util/resolveEndpoint'
import request from 'axios'


const emailRegex      = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/ // eslint-disable-line
const urlRegex        = /(http(s)?:\/\/.)?(www\.)?[-a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/ // eslint-disable-line
const passwordRegex   = /^[!@#$%^&*()_+-=A-Za-z0-9]+$/
const zipCodeRegex    = /^\d{5}([-\s]\d{4})?$/
const telephoneRegex  = /^\+1\s\d{3}\s\d{3}\s\d{2}\s\d{2}$/
const expDateRegex    = /^(0[1-9]|1[0-2])(1[6-9]|[2-9][0-9])$/


const isEmpty = (value: any): boolean => typeof value === 'undefined' || value === null || value === '' || /^\s+$/.test(value)

const fetchUserExist = (value: string): Promise<boolean> => {
  const email = value.trim().toLowerCase()

  if (!email) {
    return Promise.reject()
  }

  return request.get(resolveEndpoint('rest/user/exists'), {
    params: {
      email,
    },
  })
    .then(({ data: { exists } }) => exists)
    .catch((res: any) => {
      if (res instanceof Error) {
        return Promise.reject()
      }

      const { response: { data: { fields } } } = res

      return Promise.reject(fields && fields.message)
    })
}


export const _required = (value: any): boolean => isEmpty(value)

export const _email = (value: string): boolean => !isEmpty(value) && !emailRegex.test(value)

export const _uniqueEmail = (value: string) => fetchUserExist(value)

export const _url = (value: string): boolean => !isEmpty(value) && !urlRegex.test(value)

// availables symbols:  !  @  #  $  %  ^  &  *  (  )  _  +  -  =  A-Z  a-z  0-9
export const _password = (value: string): boolean => !isEmpty(value) && !passwordRegex.test(value)

export const _integer = (value: number): boolean => !isEmpty(value) && !Number.isInteger(Number(value))

export const _minLength = (length: number) =>
  (value: string): boolean => !isEmpty(value) && String(value).length < length

export const _maxLength = (length: number) =>
  (value: string): boolean => !isEmpty(value) && String(value).length > length

// availables symbols:  all symbols expect  <  >  /  \
export const _userName = (value: string): boolean => !isEmpty(value) && /[<>/\\]/.test(value)

// only letters not available
// only numbers not available
// availables symbols:  \s  A-Z  a-z  0-9  #  /  -
export const _streetAddress = (value: string): boolean =>
  !isEmpty(value) && (/^[A-Za-z]+$/.test(value) || /^\d+$/.test(value) || !/^[\sA-Za-z0-9#/-]+$/.test(value))

// 55555
// 55555-4444
// 5555 4444
export const _zipCode = (value: string): boolean => !isEmpty(value) && !zipCodeRegex.test(value)

// +1 999 888 77 66
export const _telephone = (value: string): boolean => !isEmpty(value) && !telephoneRegex.test(value)

/**
 * Luhn algorithm in JavaScript: validate credit card number supplied as string of numbers
 * https://gist.github.com/thensg/07bd82f73a1f784a35f0
 * https://www.freeformatter.com/credit-card-number-generator-validator.html
 */
export const _cardNumber = (cardNum: string | number): boolean => {
  if (isEmpty(cardNum)) {
    return false
  }

  if (isInteger(cardNum)) {
    cardNum = String(cardNum)
  }
  cardNum = (<string>cardNum)

  cardNum = cardNum.replace(/\s+/g, '')

  if (cardNum.length < 14) {
    return true
  }

  let sum = 0
  let digit = 0
  let even = true
  let i = cardNum.length

  const digits = [ 0, 2, 4, 6, 8, 1, 3, 5, 7, 9 ]

  while (i--) {
    digit = Number(cardNum[i])
    sum += (even = !even) ? digits[digit] : digit
  }

  if (sum <= 0 || sum % 10 !== 0) {
    return true
  }

  return false
}

// 1217 (means 12 2017)
export const _expDate = (value: string | number): boolean => {
  if (isEmpty(value)) {
    return false
  }

  if (isInteger(value)) {
    value = String(value)
  }
  value = (<string>value)

  value = value.replace('/', '')

  if (!expDateRegex.test(value)) {
    return true
  }

  const valueMonth  = Number(value.substr(0, 2))
  const valueYear   = Number(`20${value.substr(2)}`)
  const currDate    = new Date()
  const currYear    = currDate.getFullYear()
  // +1 needed 'cuz getMonth returns month from 0 to 11 🤦‍
  // https://developer.mozilla.org/ru/docs/Web/JavaScript/Reference/Global_Objects/Date/getMonth#Returns
  const currMonth   = currDate.getMonth() + 1

  return valueYear < currYear || (valueYear === currYear && valueMonth < currMonth)
}

// 999
// 9999
export const _cardCVV = (value: string | number) => !isEmpty(value) && !/^[0-9]{3,4}$/.test(String(value))

export const _equals = (fieldName: string, formxFormat: boolean) =>
  (value: any, values: { [key: string]: any }): boolean =>
    formxFormat ? value !== values[fieldName].value : value !== values[fieldName]

export const _date = (value: string): Boolean =>
  !isEmpty(value) && !moment(normalizeDateFormat(value), 'MM/DD/YYYY', true).isValid()
